/**
 * Centralized configuration module for the frontend application.
 * All environment-specific configuration should be accessed through this module.
 */

export interface AppConfig {
  api: {
    baseUrl: string;
    wsUrl: string;
  };
  env: 'development' | 'staging' | 'production';
  features: {
    enableDebug: boolean;
  };
}

/**
 * Application configuration with environment variable support and sensible defaults.
 * Environment variables are prefixed with VITE_ to be exposed to the client by Vite.
 */
// In production builds the frontend is served by the backend (same origin),
// so API and WebSocket URLs default to the page's own origin.
// MODE (not PROD/DEV) is used deliberately: `vite build` always sets MODE to
// 'production', while PROD/DEV follow NODE_ENV, which hosting platforms may
// set to other values (e.g. 'staging') at build time.
const isDevServer = import.meta.env.MODE === 'development';
const defaultApiUrl = isDevServer
  ? 'http://localhost:3001/api'
  : `${window.location.origin}/api`;
const defaultWsUrl = isDevServer
  ? 'http://localhost:3001'
  : window.location.origin;

const config: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL || defaultApiUrl,
    wsUrl: import.meta.env.VITE_WS_URL || defaultWsUrl,
  },
  env: (import.meta.env.VITE_ENV as AppConfig['env']) || 'development',
  features: {
    enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true',
  },
};

// Validate configuration in development
if (import.meta.env.DEV) {
  const requiredVars = ['VITE_API_URL', 'VITE_WS_URL'];
  const missing = requiredVars.filter(key => !import.meta.env[key]);
  
  if (missing.length > 0) {
    console.warn(
      `Missing environment variables: ${missing.join(', ')}. Using defaults.`
    );
  }
}

export default config;